import CompactDateField from "../../../../components/Forms/Input/CompactDateField";
import {
  PayablePaymentSummaryState,
} from "../store/PayablePayment.slice";

interface Props {
  data: PayablePaymentSummaryState;
  paymentDate: string;
  onPaymentDateChange: (value: string) => void;
}

const PaymentSummary = ({ data, paymentDate, onPaymentDateChange }: Props) => {
  const {
    supplier,
    number,
    total,
    paid,
    balance,
    status: backendStatus,
  } = data;

  /**
   * 🔥 fallback seguros para evitar
   * undefined desde backend / redux
   */
  const safeSupplier = supplier || "-";
  const safeNumber = number || "-";
  const safeTotal = total || 0;
  const safePaid = paid || 0;
  const safeBalance = balance || 0;

  /**
   * 🔥 Prioridad:
   * 1. status desde backend/store
   * 2. fallback calculado localmente
   */
  const status =
    backendStatus ||
    (safeBalance === 0
      ? "PAID"
      : safePaid > 0
      ? "PARTIAL"
      : "PENDING");

  const statusStyles: Record<string, string> = {
    PAID: "bg-success text-white",
    PARTIAL: "bg-warning text-black",
    PENDING: "bg-meta-6 text-black",
    OVERDUE: "bg-danger text-white",
  };

  const statusLabels: Record<string, string> = {
    PAID: "Pagado",
    PARTIAL: "Parcial",
    PENDING: "Pendiente",
    OVERDUE: "Vencido",
  };

  return (
    <div className="px-6 py-5">
      {/* 🔹 Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-black dark:text-white">
            Información de la cuenta
          </h3>

          <p className="text-sm text-graydark dark:text-gray-400">
            Detalle de la cuenta seleccionada
          </p>
        </div>

        <span
          className={`
            inline-flex rounded px-3 py-1 text-xs font-medium
            ${statusStyles[status] || "bg-meta-6 text-black"}
          `}
        >
          {statusLabels[status] || "Pendiente"}
        </span>
      </div>

      {/* 🔹 Proveedor + factura */}
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs text-graydark dark:text-gray-400">
            Proveedor
          </p>

          <p className="font-semibold text-black dark:text-white">
            {safeSupplier}
          </p>
        </div>

        <div>
          <p className="text-xs text-graydark dark:text-gray-400">
            Factura
          </p>

          <p className="text-sm font-medium text-black dark:text-white">
            {safeNumber}
          </p>
        </div>
      </div>

      {/* 🔥 Métricas: las 4 celdas comparten el mismo estilo de tarjeta */}
      <div className="grid grid-cols-2 divide-x divide-stroke overflow-hidden rounded border border-stroke dark:divide-strokedark dark:border-strokedark sm:grid-cols-4">
        <div className="bg-gray-2 p-3 dark:bg-boxdark">
          <p className="text-xs text-graydark dark:text-gray-400">
            Total
          </p>

          <p className="font-semibold text-black dark:text-white">
            ${safeTotal.toFixed(2)}
          </p>
        </div>

        <div className="bg-gray-2 p-3 dark:bg-boxdark">
          <p className="text-xs text-graydark dark:text-gray-400">
            Pagado
          </p>

          <p className="font-semibold text-success">
            ${safePaid.toFixed(2)}
          </p>
        </div>

        <div className="bg-gray-2 p-3 dark:bg-boxdark">
          <p className="text-xs text-graydark dark:text-gray-400">
            Saldo
          </p>

          <p className="font-semibold text-danger">
            ${safeBalance.toFixed(2)}
          </p>
        </div>

        {/* 🔹 Fecha de pago: única celda editable de la fila */}
        <div className="flex flex-col justify-center bg-gray-2 p-2 dark:bg-boxdark">
          <CompactDateField
            label="Fecha de pago"
            name="paymentDate"
            iconKey="calendar"
            value={paymentDate}
            onChange={onPaymentDateChange}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;